/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:52:57]
Permissions to update outside the main branch: [false]
*/
import { Button, Form, Modal, Pending, requiredRule, ValidatedInput } from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getProps } from 'axios/propApi';
import { BOProps } from 'base-components/BOProps/BOProps';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { Brand, BrandAdd } from 'models/api/brand';
import { PropExtended } from 'models/api/prop';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import 'scss/Main.scss';
import { useBrandWorkplaceState } from './Brands';

type BrandsChangeModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
};

const BrandsChangeModal = ({ isOpen = false, toggle }: BrandsChangeModalProps) => {
    const { t } = useTranslation();
    const api = useApi();
    const { hasRole } = useAuth();

    const { changeItemMutation, addItemMutation, currentItem, handleError } =
        useBrandWorkplaceState();
    const [itemProps, setItemProps] = useState<PropExtended[]>([...(currentItem?.props || [])]);

    const { isFetching } = useQuery('BrandProps', () => getProps(api, 'Brand'), {
        onSuccess: (props) => {
            setItemProps(props);
        },
        enabled: isOpen && !itemProps.length,
        onError: (e: AxiosError<AxiosErrorResponse>) => handleError(e.response?.data),
    });

    const saveChanges = async (brand: Brand) => {
        brand.props.forEach((p) => {
            if (p.values) {
                // remove empty values
                p.values = p.values.filter((v) => v.value);
            }
        });

        const formattedItem: BrandAdd = {
            code: brand.code,
            name: brand.name,
            dsc: brand.dsc,
            isActive: !!brand.isActive,
            props: brand.props,
        };

        brand.id
            ? changeItemMutation.mutate({ ...formattedItem, id: brand.id })
            : addItemMutation.mutate({ ...formattedItem });

        toggle(false);
    };

    const formHasRole = useMemo(
        () => (currentItem ? hasRole('brandsCreate') : hasRole('brandsUpdate')),
        [hasRole]
    );

    useEffect(
        () => setItemProps((prev) => prev.map((x) => ({ ...x, disabled: !formHasRole }))),
        [formHasRole]
    );

    const { id = '', isActive = false, name = '', dsc = '', code = '' } = currentItem || {};

    const defaultValues = {
        id,
        isActive,
        name,
        dsc,
        code,
        props: itemProps,
    };

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={() => toggle(false)}
            scrollable
            className="brands__modal"
            data-testid="brands-modal"
        >
            {isFetching ? (
                <Pending />
            ) : (
                <Form<Brand>
                    onSubmit={saveChanges}
                    defaultValues={defaultValues}
                    className="form-in-modal"
                >
                    <ModalHeader toggle={() => toggle(false)}>
                        {id ? `#${id}` : t('BackOfficeNew')}
                    </ModalHeader>
                    <ModalBody className="custom-scrollbar">
                        <Row>
                            <Col>
                                <ValidatedInput<Brand>
                                    type="checkbox"
                                    name="isActive"
                                    disabled={!formHasRole}
                                    label={t('BackOfficeIsActive')}
                                />
                            </Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col>
                                <ValidatedInput<Brand>
                                    id="info-code-field"
                                    name="code"
                                    label={t('BackOfficeCode')}
                                    disabled={!!id || !formHasRole}
                                    rules={{
                                        required: requiredRule,
                                    }}
                                />
                            </Col>
                            <Col>
                                <ValidatedInput<Brand>
                                    id="info-name-field"
                                    name="name"
                                    label={t('BackOfficeName')}
                                    disabled={!formHasRole}
                                    rules={{
                                        required: requiredRule,
                                    }}
                                />
                            </Col>
                        </Row>
                        <hr />
                        <BOProps<Brand> name="props" />
                        <hr />
                        <ValidatedInput<Brand>
                            id="info-dsc-field"
                            name="dsc"
                            label={t('BackOfficeDescription')}
                            type="textarea"
                            disabled={!formHasRole}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => toggle(false)}>
                            <Trans i18nKey="BackOfficeCancel" />
                        </Button>
                        <Button color="success" type="submit" disabled={!formHasRole || isFetching}>
                            <Trans i18nKey="BackOfficeSave" />
                        </Button>
                    </ModalFooter>
                </Form>
            )}
        </Modal>
    );
};

export default BrandsChangeModal;
